/**
 * ZAdminFeatureFlags - Zod Validation Schemas for the Admin Feature Flags API
 *
 * Single source of truth for the admin feature-flags surface. These schemas
 * generate both runtime validation (Zod) and compile-time types (TApi).
 *
 * Admin endpoint - protected by AdminPermissionGuard (ROOT / admin only).
 * Base path: /api/admin/feature-flags
 *
 * Note: this is distinct from `ZFeatureFlags` (org-facing, `/api/feature-flags`).
 * IDs on this surface are Clerk identifiers (`org_*`, `user_*`) or DB UUIDs, so
 * every id/timestamp field stays `z.string()` — never `.uuid()`/`.email()` — to
 * avoid rejecting real rows at runtime parse.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// ENTITY SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * Organization override on a feature flag, with audit trail.
 * Mirrors ListAllFlags / GetFlagDetail use-case output (timestamps are ISO strings).
 */
export const FeatureFlagOverrideSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  /// Display name for the overrides list. Null when the org row is gone (an
  /// override outlives a deleted org) — the UI falls back to the id.
  /// `.catch(null)` so a newer SPA against an older backend degrades.
  organizationName: z.string().nullable().catch(null),
  enabled: z.boolean(),
  enabledBy: z.string().nullable(),
  enabledAt: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type FeatureFlagOverride = z.infer<typeof FeatureFlagOverrideSchema>;

/**
 * User override on a feature flag (cross-org), enriched with user identity.
 */
export const UserFeatureFlagOverrideSchema = z.object({
  id: z.string(),
  userId: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  enabled: z.boolean(),
  enabledBy: z.string().nullable(),
  enabledAt: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type UserFeatureFlagOverride = z.infer<typeof UserFeatureFlagOverrideSchema>;

/**
 * Why this flag cannot be turned on right now, or `null` when nothing blocks it.
 *
 * Set for a flag whose metadata names a platform variable that must be present
 * before the flag may be enabled anywhere, while that variable is absent from
 * the running backend. The admin screen shows it where the flag is toggled, and
 * every enabling route refuses with the same sentence, so the screen and the
 * server cannot disagree. `.catch(null)` so a newer SPA against an older
 * backend, which omits the field, degrades to "nothing blocks it" — the server
 * still refuses.
 */
const EnableBlockedReasonSchema = z.string().nullable().catch(null);

/**
 * Feature flag summary row for the list view (with aggregate override stats).
 */
export const FeatureFlagSummarySchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.string(),
  globalEnabled: z.boolean(),
  /// False when the code catalog no longer defines this key — the row outlived
  /// its TypeScript definition, gates nothing, and cannot be toggled (every
  /// write route rejects an uncatalogued key). Its one remaining action is
  /// `DeleteOrphan`. `.catch(true)` so a newer SPA against an older backend,
  /// which omits the field, degrades to "assume live" rather than flagging every
  /// row as dead.
  catalogued: z.boolean().catch(true),
  enableBlockedReason: EnableBlockedReasonSchema,
  overrideStats: z.object({
    enabled: z.number(),
    disabled: z.number(),
    total: z.number()
  }),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type FeatureFlagSummary = z.infer<typeof FeatureFlagSummarySchema>;

/**
 * Full feature flag detail: global config, all overrides, and statistics.
 */
export const FeatureFlagDetailSchema = z.object({
  flag: z.object({
    key: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    category: z.string(),
    globalEnabled: z.boolean(),
    enableBlockedReason: EnableBlockedReasonSchema,
    createdAt: z.string(),
    updatedAt: z.string()
  }),
  overrides: z.array(FeatureFlagOverrideSchema),
  userOverrides: z.array(UserFeatureFlagOverrideSchema),
  stats: z.object({
    totalOrgs: z.number(),
    overridesEnabled: z.number(),
    overridesDisabled: z.number(),
    orgsWithGlobalDefault: z.number(),
    userOverridesEnabled: z.number(),
    userOverridesDisabled: z.number()
  })
});
export type FeatureFlagDetail = z.infer<typeof FeatureFlagDetailSchema>;

/**
 * User search result for adding user overrides (firstName/lastName omitted when null).
 */
export const AdminFeatureFlagUserSearchResultSchema = z.object({
  userId: z.string(),
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional()
});
export type AdminFeatureFlagUserSearchResult = z.infer<
  typeof AdminFeatureFlagUserSearchResultSchema
>;

/**
 * What clearing an orphan key's overrides actually removed.
 *
 * Reported rather than inferred: the operator asked for "all of them", so only
 * the reply can say how many that was, and the same numbers are what make the
 * audit record readable after the fact.
 */
export const ClearedOrphanOverridesSchema = z.object({
  organizationOverrides: z.number(),
  userOverrides: z.number()
});
export type ClearedOrphanOverrides = z.infer<typeof ClearedOrphanOverridesSchema>;

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const FeatureFlagKeyPathVarsSchema = z.object({ key: z.string() });
export const FeatureFlagOrgPathVarsSchema = z.object({
  key: z.string(),
  orgId: z.string()
});
export const FeatureFlagUserPathVarsSchema = z.object({
  key: z.string(),
  userId: z.string()
});

/** Toggle the global default for a flag. */
export const UpdateFeatureFlagGlobalBodySchema = z.object({ enabled: z.boolean() });

/** Optional reason attached to an enable/disable override action. */
export const FeatureFlagOverrideActionBodySchema = z.object({
  reason: z.string().optional()
});

/** Search users by email for user overrides. */
export const SearchFeatureFlagUsersQuerySchema = z.object({
  q: z.string(),
  limit: z.coerce.number().int().min(1).max(50).optional()
});

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const ListFeatureFlagsResponseSchema = z.object({
  flags: z.array(FeatureFlagSummarySchema)
});
export type ListFeatureFlagsResponse = z.infer<typeof ListFeatureFlagsResponseSchema>;

export const SearchFeatureFlagUsersResponseSchema = z.array(AdminFeatureFlagUserSearchResultSchema);
export type SearchFeatureFlagUsersResponse = z.infer<typeof SearchFeatureFlagUsersResponseSchema>;

/**
 * Evaluation of one flag against one organization, with the provenance of the answer.
 *
 * `source` mirrors `CheckFeatureEnabledUseCase`'s resolution order exactly:
 * - `suppressed`    — the flag declares a `suppressedBy` master switch and that switch
 *   is ON for this organization, so the answer is `false` whatever the rest of the
 *   ladder said. Outranks every entry below it, including `user_override`. This is the
 *   provenance an admin needs when a flag reads OFF while its own override reads ON —
 *   without it the admin panel shows a contradiction and no way to explain it.
 * - `user_override` — a per-user override won (only reachable when a userId is supplied;
 *   the admin check route never supplies one, but the producer's union includes it)
 * - `override`      — an organization override won, or the org-layer cache answered
 *   (the cache cannot distinguish an override from a global fallback)
 * - `global`        — no override; the flag's global default was used
 * - `default`       — the flag has no row at all; the safe default `false` was used
 */
export const FeatureFlagCheckResultSchema = z.object({
  enabled: z.boolean(),
  source: z.enum(["suppressed", "user_override", "override", "global", "default"])
});
export type FeatureFlagCheckResult = z.infer<typeof FeatureFlagCheckResultSchema>;

// ═══════════════════════════════════════════════════════════════
// ZADMIN_FEATURE_FLAGS MANIFEST
// ═══════════════════════════════════════════════════════════════

/**
 * ZAdminFeatureFlags - Zod API validation schemas for the admin feature-flags surface.
 *
 * Mutations return `null` (`ApiSuccessSchema(z.null())`) — the controller wraps
 * `createApiSuccess(null)` so the generated hook's runtime parse succeeds.
 *
 * Static route (`users/search`) is declared before the `:key` route so route
 * ordering matches the controller.
 */
export const ZAdminFeatureFlags = {
  /**
   * List all feature flags with override statistics.
   * GET /api/admin/feature-flags
   */
  List: {
    method: "GET" as const,
    path: "/api/admin/feature-flags",
    Response: ApiSuccessSchema(ListFeatureFlagsResponseSchema)
  },

  /**
   * Search users by email for user overrides (max 5, min 3-char query).
   * GET /api/admin/feature-flags/users/search
   */
  SearchUsers: {
    method: "GET" as const,
    path: "/api/admin/feature-flags/users/search",
    Query: SearchFeatureFlagUsersQuerySchema,
    Response: ApiSuccessSchema(SearchFeatureFlagUsersResponseSchema)
  },

  /**
   * Delete an ORPHAN flag row — one whose key the code catalog no longer defines.
   * DELETE /api/admin/feature-flags/orphans/:key
   *
   * Declared before the `:key` routes, matching the controller's declaration
   * order, so the static `orphans` segment is not swallowed by a `:key` match.
   *
   * Refuses (409) while any organization or user override still references the
   * key: `FeatureFlag.key` cascades to both children, so deleting a row with
   * overrides would destroy configuration rather than clear dead weight.
   */
  DeleteOrphan: {
    method: "DELETE" as const,
    path: "/api/admin/feature-flags/orphans/:key",
    PathVars: FeatureFlagKeyPathVarsSchema,
    Response: ApiSuccessSchema(z.null())
  },

  /**
   * Clear every override row an ORPHAN key still carries.
   * DELETE /api/admin/feature-flags/orphans/:key/overrides
   *
   * This is the exit from a loop the orphan surface used to have no way out of:
   * `DeleteOrphan` refuses while overrides exist, and every override-removal
   * route on the operating surface narrows its `:key` through
   * `ParseFeatureFlagKeyPipe`, which rejects a key the catalog does not define.
   * So the only route that could take the count to zero refused exactly the keys
   * that needed it, and such a row was permanently unclearable.
   *
   * It is a SECOND deliberate act rather than a cascade inside `DeleteOrphan`.
   * The 409 exists so nothing is destroyed by a delete aimed at the row, and
   * folding the clear into it would remove that protection instead of giving it
   * an exit. The response reports what went, so the operator and the audit
   * record both carry the number rather than inferring it.
   *
   * Safe for the same reason `DeleteOrphan` is: every evaluation path is typed
   * on `FeatureFlagKey`, so an override on an uncatalogued key is unreachable
   * configuration and removing it cannot change how any feature resolves.
   */
  ClearOrphanOverrides: {
    method: "DELETE" as const,
    path: "/api/admin/feature-flags/orphans/:key/overrides",
    PathVars: FeatureFlagKeyPathVarsSchema,
    Response: ApiSuccessSchema(ClearedOrphanOverridesSchema)
  },

  /**
   * Get detailed information about a specific flag.
   * GET /api/admin/feature-flags/:key
   */
  GetDetail: {
    method: "GET" as const,
    path: "/api/admin/feature-flags/:key",
    PathVars: FeatureFlagKeyPathVarsSchema,
    Response: ApiSuccessSchema(FeatureFlagDetailSchema)
  },

  /**
   * Update the global default for a flag.
   * PATCH /api/admin/feature-flags/:key/global
   */
  UpdateGlobal: {
    method: "PATCH" as const,
    path: "/api/admin/feature-flags/:key/global",
    PathVars: FeatureFlagKeyPathVarsSchema,
    Body: UpdateFeatureFlagGlobalBodySchema,
    Response: ApiSuccessSchema(z.null())
  },

  /**
   * Whitelist an organization for a feature.
   * POST /api/admin/feature-flags/:key/organizations/:orgId/enable
   */
  EnableForOrg: {
    method: "POST" as const,
    path: "/api/admin/feature-flags/:key/organizations/:orgId/enable",
    PathVars: FeatureFlagOrgPathVarsSchema,
    Body: FeatureFlagOverrideActionBodySchema,
    Response: ApiSuccessSchema(z.null())
  },

  /**
   * Blacklist an organization for a feature.
   * POST /api/admin/feature-flags/:key/organizations/:orgId/disable
   */
  DisableForOrg: {
    method: "POST" as const,
    path: "/api/admin/feature-flags/:key/organizations/:orgId/disable",
    PathVars: FeatureFlagOrgPathVarsSchema,
    Body: FeatureFlagOverrideActionBodySchema,
    Response: ApiSuccessSchema(z.null())
  },

  /**
   * Remove an organization override (fall back to global default).
   * DELETE /api/admin/feature-flags/:key/organizations/:orgId
   */
  RemoveOrgOverride: {
    method: "DELETE" as const,
    path: "/api/admin/feature-flags/:key/organizations/:orgId",
    PathVars: FeatureFlagOrgPathVarsSchema,
    Response: ApiSuccessSchema(z.null())
  },

  /**
   * Whitelist a user for a feature (cross-org).
   * POST /api/admin/feature-flags/:key/users/:userId/enable
   */
  EnableForUser: {
    method: "POST" as const,
    path: "/api/admin/feature-flags/:key/users/:userId/enable",
    PathVars: FeatureFlagUserPathVarsSchema,
    Body: FeatureFlagOverrideActionBodySchema,
    Response: ApiSuccessSchema(z.null())
  },

  /**
   * Blacklist a user for a feature (cross-org).
   * POST /api/admin/feature-flags/:key/users/:userId/disable
   */
  DisableForUser: {
    method: "POST" as const,
    path: "/api/admin/feature-flags/:key/users/:userId/disable",
    PathVars: FeatureFlagUserPathVarsSchema,
    Body: FeatureFlagOverrideActionBodySchema,
    Response: ApiSuccessSchema(z.null())
  },

  /**
   * Remove a user override (fall back to org / global default).
   * DELETE /api/admin/feature-flags/:key/users/:userId
   */
  RemoveUserOverride: {
    method: "DELETE" as const,
    path: "/api/admin/feature-flags/:key/users/:userId",
    PathVars: FeatureFlagUserPathVarsSchema,
    Response: ApiSuccessSchema(z.null())
  },

  /**
   * Evaluate a flag against an organization and report why (debugging aid).
   * POST /api/admin/feature-flags/:key/check/:orgId
   *
   * POST rather than GET because the evaluation populates the flag cache as a
   * side effect; the shape is otherwise a read.
   */
  Check: {
    method: "POST" as const,
    path: "/api/admin/feature-flags/:key/check/:orgId",
    PathVars: FeatureFlagOrgPathVarsSchema,
    Response: ApiSuccessSchema(FeatureFlagCheckResultSchema)
  }
} as const;
